import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    LinearProgress,
    Alert,
    Snackbar,
    Collapse,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    styled,
    Switch,
    FormControlLabel,
    ListSubheader,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';

// Стилизованные компоненты
const SectionHeaderRow = styled(TableRow)(({ theme }) => ({
    background: 'linear-gradient(135deg, #1a5276 0%, #2874a6 100%)',
    '&:hover': {
        background: 'linear-gradient(135deg, #154360 0%, #21618c 100%)',
    },
    cursor: 'pointer',
    boxShadow: theme.shadows[2],
}));

const HeaderText = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.white,
    fontWeight: 'bold',
    padding: theme.spacing(1),
    fontSize: theme.breakpoints.down('sm') ? '0.875rem' : '1rem'
}));

const SubHeaderRow = styled(TableRow)(({ theme }) => ({
    background: 'linear-gradient(to right, #3498db, #5dade2)',
    '& th': {
        color: theme.palette.common.white,
        fontWeight: 'bold',
        fontSize: '0.875rem',
    },
}));

const SubHeaderText = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.white,
    fontWeight: 'bold',
    fontSize: theme.breakpoints.down('sm') ? '0.75rem' : '0.875rem'
}));

const StyledTableRow = styled(TableRow)(({ theme, even, missing }) => ({
    backgroundColor: even ? theme.palette.action.hover : theme.palette.background.paper,
    ...(missing && {
        backgroundColor: theme.palette.error.light,
        '& td': {
            color: theme.palette.error.dark,
            fontWeight: 'bold'
        },
        '&:hover': {
            backgroundColor: theme.palette.error.main,
        }
    }),
    '& td': {
        padding: theme.breakpoints.down('sm') ? theme.spacing(0.5) : theme.spacing(1),
    }
}));

const MissingValueText = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.black,
    fontStyle: 'italic',
    fontSize: theme.breakpoints.down('sm') ? '0.75rem' : '0.875rem'
}));

const StyledLink = styled('a')(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
        color: theme.palette.primary.dark,
    },
    fontSize: theme.breakpoints.down('sm') ? '0.75rem' : '0.875rem'
}));

const MultiValueContainer = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.5, 0),
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
}));

const MultiValueHeader = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
    },
}));

const MultiValueContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    maxHeight: '200px',
    overflowY: 'auto',
}));

const TextTableCell = styled(TableCell)(({ theme }) => ({
    maxWidth: theme.breakpoints.down('sm') ? '150px' : '400px',
    wordBreak: 'break-all',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': {
        whiteSpace: 'normal',
        overflow: 'visible',
        position: 'relative',
        zIndex: theme.zIndex.tooltip,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[3],
    },
}));

const ModalTableCell = styled(TableCell)(({ theme }) => ({
    maxWidth: theme.breakpoints.down('sm') ? '100px' : '300px',
    wordBreak: 'break-all',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: theme.breakpoints.down('sm') ? '0.75rem' : '0.875rem',
    '&:hover': {
        whiteSpace: 'normal',
        overflow: 'visible',
        position: 'relative',
        zIndex: theme.zIndex.tooltip,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[3],
    },
}));

// Компонент кругового индикатора
const CircularProgressWithLabel = ({ value, size = 120, thickness = 8 }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const getColor = (percent) => {
        if (percent > 70) return '#4caf50';
        if (percent > 40) return '#ff9800';
        return '#f44336';
    };

    const adjustedSize = isMobile ? size * 0.7 : size;
    const adjustedThickness = isMobile ? thickness * 0.8 : thickness;

    return (
        <Box position="relative" display="inline-flex" sx={{ mb: 3 }}>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: adjustedSize,
                    height: adjustedSize,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: '50%',
                        backgroundColor: '#f5f5f5',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: '50%',
                        background: `conic-gradient(${getColor(value)} ${value * 3.6}deg, transparent 0deg)`,
                        transform: 'rotate(-90deg)',
                        transformOrigin: 'center',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: adjustedThickness,
                        left: adjustedThickness,
                        right: adjustedThickness,
                        bottom: adjustedThickness,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        component="div"
                        color="textPrimary"
                        sx={{ fontWeight: 'bold' }}
                    >
                        {`${Math.round(value)}%`}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const UserResults = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [results, setResults] = useState([]);
    const [groupedResults, setGroupedResults] = useState({});
    const [ous, setOus] = useState([]);
    const [selectedOu, setSelectedOu] = useState(null);
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [isChecking, setIsChecking] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openSections, setOpenSections] = useState({});
    const [openMultiValues, setOpenMultiValues] = useState({});
    const [openModal, setOpenModal] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [currentTableName, setCurrentTableName] = useState('');
    const [completionPercentage, setCompletionPercentage] = useState(0);
    const [showOnlyMissing, setShowOnlyMissing] = useState(false);
    const selectedOuData = ous.find(ou => ou.id === selectedOu);

    const handleToggleMultiValue = (key) => {
        setOpenMultiValues(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleOpenModal = (htmlContent, tableName) => {
        setModalContent(htmlContent);
        setCurrentTableName(tableName);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setModalContent('');
        setCurrentTableName('');
    };

    const renderModalTable = () => {
        if (!modalContent) return null;

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(modalContent, 'text/html');
            const table = doc.querySelector('table');

            if (!table) {
                return <Typography>{modalContent}</Typography>;
            }

            const rows = Array.from(table.querySelectorAll('tr'));
            const headers = Array.from(table.querySelectorAll('th'));

            return (
                <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                    <Table stickyHeader size={isMobile ? "small" : "medium"}>
                        {headers.length > 0 && (
                            <TableHead>
                                <TableRow>
                                    {headers.map((header, index) => (
                                        <ModalTableCell key={`header-${index}`} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                            {header.textContent}
                                        </ModalTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                        )}
                        <TableBody>
                            {rows.map((row, rowIndex) => {
                                if (headers.length > 0 && row.querySelector('th')) return null;

                                const cells = Array.from(row.querySelectorAll('td'));
                                return (
                                    <TableRow key={`modal-row-${rowIndex}`}>
                                        {cells.map((cell, cellIndex) => (
                                            <ModalTableCell key={`modal-cell-${rowIndex}-${cellIndex}`}>
                                                <div
                                                    style={{
                                                        maxHeight: '100px',
                                                        overflow: 'auto',
                                                        wordBreak: 'break-all',
                                                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: cell.innerHTML }}
                                                />
                                            </ModalTableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        } catch (error) {
            console.error('Ошибка при разборе таблицы:', error);
            return (
                <Typography color="error">
                    Ошибка при отображении таблицы: {error.message}
                </Typography>
            );
        }
    };

    useEffect(() => {
        fetchUserOUs();
    }, []);

    useEffect(() => {
        if (selectedOu) {
            fetchResults(selectedOu);
        }
    }, [selectedOu]);

    const fetchUserOUs = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.apiBaseUrl}/admin/ou-links`, {
                headers: { 'x-access-token': token },
            });

            const userResponse = await axios.get(`${config.apiBaseUrl}/admin/user`, {
                headers: { 'x-access-token': token },
            });

            const allowedOUs = userResponse.data.allowed_ou_ids || [];
            const filteredOUs = allowedOUs.length === 0
                ? response.data
                : response.data.filter(ou => allowedOUs.includes(ou.id));

            setOus(filteredOUs);

            if (filteredOUs.length === 1) {
                setSelectedOu(filteredOUs[0].id);
            }

            setLoading(false);
        } catch (err) {
            console.error('Ошибка при загрузке данных:', err);
            setError('Ошибка при загрузке данных');
            setLoading(false);
        }
    };

    const fetchResults = async (ouId) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await axios.get(`${config.apiBaseUrl}/admin/results?ou_id=${ouId}`, {
                headers: { 'x-access-token': token },
            });

            if (!response.data || !Array.isArray(response.data)) {
                console.error("Invalid data format:", response.data);
                setResults([]);
                setGroupedResults({});
                setLoading(false);
                return;
            }

            const completionResponse = await axios.get(`${config.apiBaseUrl}/admin/completionpercentage?ou_id=${ouId}`, {
                headers: { 'x-access-token': token },
            });

            setResults(response.data);
            groupResultsByCategory(response.data);
            setCompletionPercentage(completionResponse.data.completion_percentage || 0);
            setLoading(false);
        } catch (err) {
            console.error('Ошибка при загрузке результатов:', err);
            setError('Ошибка при загрузке результатов');
            setLoading(false);
        }
    };

    const groupResultsByCategory = (data) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            console.error("Invalid data format in groupResultsByCategory:", data);
            setGroupedResults({});
            return;
        }

        const sectionTitles = {
            '/sveden/common': 'Основные сведения',
            '/sveden/struct': 'Структура и органы управления',
            '/sveden/document': 'Документы',
            '/sveden/education': 'Образование',
            '/sveden/edustandarts': 'Образовательные стандарты',
            '/sveden/managers': 'Руководство',
            '/sveden/employees': 'Педагогический состав',
            '/sveden/objects': 'Материально-техническое обеспечение',
            '/sveden/grants': 'Стипендии и меры поддержки',
            '/sveden/paid_edu': 'Платные образовательные услуги',
            '/sveden/budget': 'Финансово-хозяйственная деятельность',
            '/sveden/vacant': 'Вакантные места',
            '/sveden/inter': 'Международное сотрудничество',
            '/sveden/catering': 'Организация питания'
        };

        const groupedBySection = {};

        data.forEach(item => {
            const sectionPath = item.section_path || getSectionFromUrl(item.url);
            const sectionTitle = sectionTitles[sectionPath] || sectionPath;

            if (!groupedBySection[sectionPath]) {
                groupedBySection[sectionPath] = {
                    title: sectionTitle,
                    items: {},
                    tables: {}
                };
            }

            const isTable = item.tag.includes('_table_');
            if (isTable) {
                const mainTag = item.tag.split('_table_')[0];
                if (!groupedBySection[sectionPath].tables[mainTag]) {
                    groupedBySection[sectionPath].tables[mainTag] = [];
                }
                groupedBySection[sectionPath].tables[mainTag].push(item);
                return;
            }

            const groupKey = `${item.indicator_number || ''}_${item.tag}_${item.field_name || ''}`;

            if (!groupedBySection[sectionPath].items[groupKey]) {
                groupedBySection[sectionPath].items[groupKey] = {
                    indicator_number: item.indicator_number,
                    tag: item.tag,
                    field_name: item.field_name || `Тег ${item.tag}`,
                    values: [],
                    missing: item.missing,
                    section_path: sectionPath,
                    uniqueKey: `${item.tag}_${item.indicator_number}_${Math.random().toString(36).substr(2, 5)}`
                };
            }

            const processedValue = processValue(item.value);
            groupedBySection[sectionPath].items[groupKey].values.push(processedValue);
        });

        Object.keys(groupedBySection).forEach(sectionPath => {
            groupedBySection[sectionPath].items = Object.values(groupedBySection[sectionPath].items);
            groupedBySection[sectionPath].items.sort((a, b) => {
                return (a.indicator_number || '').localeCompare(b.indicator_number || '', undefined, { numeric: true });
            });

            Object.values(groupedBySection[sectionPath].tables).forEach(tables => {
                tables.sort((a, b) => a.tag.localeCompare(b.tag));
            });
        });

        if (config.paths) {
            Object.entries(config.paths).forEach(([sectionPath, tags]) => {
                if (!groupedBySection[sectionPath]) {
                    groupedBySection[sectionPath] = {
                        title: sectionTitles[sectionPath] || sectionPath,
                        items: [],
                        tables: {}
                    };
                }

                if (Array.isArray(tags)) {
                    tags.forEach(tagConfig => {
                        const exists = groupedBySection[sectionPath].items.some(
                            item => item.tag === tagConfig.tag && item.indicator_number === tagConfig.n
                        );

                        if (!exists) {
                            groupedBySection[sectionPath].items.push({
                                tag: tagConfig.tag,
                                field_name: tagConfig.name,
                                values: ['Тег не найден'],
                                missing: true,
                                indicator_number: tagConfig.n,
                                section_path: sectionPath,
                                uniqueKey: `${tagConfig.tag}_${tagConfig.n}`
                            });
                        }
                    });
                }
            });
        }

        const orderedSections = {};
        Object.keys(sectionTitles).forEach(sectionPath => {
            if (groupedBySection[sectionPath]) {
                orderedSections[sectionPath] = groupedBySection[sectionPath];
            }
        });

        Object.keys(groupedBySection).forEach(sectionPath => {
            if (!orderedSections[sectionPath]) {
                orderedSections[sectionPath] = groupedBySection[sectionPath];
            }
        });

        setGroupedResults(orderedSections);

        const sectionsState = {};
        Object.keys(orderedSections).forEach(sectionPath => {
            sectionsState[sectionPath] = true;
        });
        setOpenSections(sectionsState);
    };

    const processValue = (value) => {
        if (value === null || value === undefined) return 'Нет данных';

        const stringValue = typeof value === 'string' ? value : String(value);

        if (Array.isArray(value)) return value;

        if (typeof stringValue === 'string' && stringValue.includes('|')) {
            return stringValue.split('|').map(v => v.trim()).filter(v => v);
        }

        if (stringValue.toLowerCase().includes('спам-бот')) {
            return 'защита от спам-ботов';
        }

        if (stringValue.includes('document.getElementById') && stringValue.includes('innerHTML')) {
            const emailMatch = stringValue.match(/var\s+prefix\s*=\s*'([^']+)'/);
            if (emailMatch && emailMatch[1]) {
                return emailMatch[1].replace(/\\/g, '');
            }
            return 'защита от спам-ботов';
        }

        return stringValue;
    };

    const handleCheckWebsite = async () => {
        try {
            setIsChecking(true);
            setProgress(0);

            const token = localStorage.getItem('token');
            const ou = ous.find(ou => ou.id === selectedOu);

            const eventSource = new EventSource(
                `${config.apiBaseUrl}/admin/check-website?url=${encodeURIComponent(ou.link)}&token=${token}&ouId=${selectedOu}`
            );

            eventSource.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.progress !== undefined) {
                    setProgress(data.progress);
                }
                if (data.message) {
                    setSuccess(data.message);
                }
                if (data.success) {
                    eventSource.close();
                    setIsChecking(false);
                    fetchResults(selectedOu);
                }
            };

            eventSource.onerror = () => {
                eventSource.close();
                setIsChecking(false);
                setError('Ошибка при проверке сайта');
            };

        } catch (err) {
            console.error('Ошибка при запуске проверки:', err);
            setError('Ошибка при запуске проверки');
            setIsChecking(false);
        }
    };

    const exportToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Результаты проверки');

            const headerStyle = {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
                font: { bold: true, color: { argb: 'FFFFFFFF' } },
                alignment: { horizontal: 'center', vertical: 'middle' },
                border: {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } }
                }
            };

            worksheet.columns = [
                { header: '№', width: 10 },
                { header: 'Раздел', width: 30 },
                { header: 'Наименование', width: 40 },
                { header: 'Тег', width: 20 },
                { header: 'Значение', width: 60 }
            ];

            const headerRow = worksheet.getRow(1);
            headerRow.eachCell((cell) => {
                cell.style = headerStyle;
            });

            let rowIndex = 2;

            Object.entries(groupedResults).forEach(([sectionPath, sectionData]) => {
                const sectionRow = worksheet.getRow(rowIndex);
                sectionRow.getCell(2).value = sectionData.title;
                sectionRow.getCell(2).style = {
                    font: { bold: true, size: 14 },
                    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } }
                };
                worksheet.mergeCells(`B${rowIndex}:E${rowIndex}`);
                rowIndex++;

                sectionData.items.forEach(item => {
                    if (item.tag.includes('_table_')) return;

                    const isSubsection = item.indicator_number && item.indicator_number.includes('.');
                    const indentLevel = isSubsection ? item.indicator_number.split('.').length - 1 : 0;
                    const indent = '  '.repeat(indentLevel);

                    const values = Array.isArray(item.values) ? item.values : [item.value];
                    values.forEach((value, valueIndex) => {
                        const row = worksheet.getRow(rowIndex);

                        let displayNumber = item.indicator_number;
                        if (isSubsection && valueIndex > 0) {
                            const parts = item.indicator_number.split('.');
                            displayNumber = `${parts[0]}.${parseInt(parts[1]) + valueIndex}`;
                        }

                        row.getCell(1).value = displayNumber || '';
                        row.getCell(2).value = sectionData.title;
                        row.getCell(3).value = `${indent}${item.field_name}`;
                        row.getCell(4).value = item.tag;

                        const cellValue = value;
                        if (cellValue === 'Тег не найден') {
                            row.getCell(5).value = 'Отсутствует';
                            row.getCell(5).style = { font: { color: { argb: 'FFFF0000' }, italic: true } };
                        } else if (typeof cellValue === 'string' && cellValue.startsWith('http')) {
                            row.getCell(5).value = { text: cellValue, hyperlink: cellValue };
                            row.getCell(5).style = { font: { color: { argb: 'FF0563C1' }, underline: true } };
                        } else if (typeof cellValue === 'string' && cellValue.startsWith('<table')) {
                            row.getCell(5).value = 'Таблица (см. в веб-интерфейсе)';
                        } else {
                            row.getCell(5).value = cellValue !== null && cellValue !== undefined ? String(cellValue) : 'Нет данных';
                        }

                        rowIndex++;
                    });
                });

                Object.values(sectionData.tables || {}).forEach(tableItems => {
                    tableItems.forEach(tableItem => {
                        const row = worksheet.getRow(rowIndex);
                        row.getCell(1).value = tableItem.indicator_number || '';
                        row.getCell(2).value = sectionData.title;
                        row.getCell(3).value = tableItem.field_name;
                        row.getCell(4).value = tableItem.tag;

                        const tableValue = tableItem.value;
                        if (typeof tableValue === 'string' && tableValue.startsWith('=HYPERLINK')) {
                            const url = tableValue.match(/http[^"]+/)?.[0] || '#';
                            row.getCell(5).value = { text: 'Открыть таблицу', hyperlink: url };
                            row.getCell(5).style = {
                                font: { color: { argb: 'FF0563C1' }, underline: true }
                            };
                        } else {
                            row.getCell(5).value = tableValue !== null && tableValue !== undefined ? String(tableValue) : 'Нет данных';
                        }

                        rowIndex++;
                    });
                });

                rowIndex++;
            });

            const ouNumber = ous.find(ou => ou.id === selectedOu)?.number || selectedOu;
            const fileName = `Результаты_проверки_ОУ_№${ouNumber}_${new Date().toLocaleDateString('ru-RU')}.xlsx`;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            saveAs(blob, fileName);

        } catch (error) {
            console.error('Ошибка при экспорте в Excel:', error);
            setError('Не удалось экспортировать данные в Excel');
        }
    };

    const processText = (text) => {
        if (!text || typeof text !== 'string') return text || 'Нет данных';

        if (text.toLowerCase().includes('спам-бот')) {
            return 'защита от спам-ботов';
        }

        if (text.startsWith('/downloads/')) {
            const parts = text.split('/');
            const site = parts[2];
            const filename = parts[3];
            const downloadUrl = `${config.apiBaseUrl}/admin/downloads/${site}/${filename}`;

            return (
                <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Посмотреть файл
                </Button>
            );
        }

        if (text.length > (isMobile ? 50 : 150)) {
            return (
                <>
                    {text.substring(0, isMobile ? 50 : 150)}...
                    <Button
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setModalContent(text);
                            setOpenModal(true);
                        }}
                    >
                        Подробнее
                    </Button>
                </>
            );
        }
        return text;
    };

    const handleToggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const filterResults = (items) => {
        if (!items || typeof items !== 'object' || Object.keys(items).length === 0) {
            return {};
        }

        if (!showOnlyMissing) return items;

        const filtered = {};

        Object.entries(items).forEach(([sectionPath, sectionData]) => {
            const filteredItems = sectionData.items.filter(item => {
                return (
                    item.missing ||
                    (Array.isArray(item.values) && item.values.some(val => val === 'Тег не найден')) ||
                    item.values.some(val => val === null || val === undefined || val === '')
                );
            });

            if (filteredItems.length > 0) {
                filtered[sectionPath] = {
                    ...sectionData,
                    items: filteredItems
                };
            }
        });

        return filtered;
    };

    const getSectionFromUrl = (url) => {
        if (!url) return '/sveden/common';

        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/').filter(Boolean);

            const knownSections = Object.keys(config.paths);
            for (const section of knownSections) {
                const sectionKey = section.replace('/sveden/', '');
                if (pathParts.includes(sectionKey)) {
                    return section;
                }
            }

            return '/sveden/common';
        } catch (e) {
            console.error('Error parsing URL:', e);
            return '/sveden/common';
        }
    };

    const renderTable = () => {
        if (!groupedResults || Object.keys(groupedResults).length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={4} align="center">
                        <Typography>
                            {loading ? "Загрузка..." : "Нет данных для отображения"}
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        }

        const filteredResults = filterResults(groupedResults);

        if (Object.keys(filteredResults).length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={4} align="center">
                        <Typography>
                            {showOnlyMissing
                                ? "Все теги заполнены - отсутствующих не найдено"
                                : "Нет данных для отображения"}
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        }

        return Object.entries(filteredResults).map(([sectionPath, sectionData]) => {
            const hasVisibleItems = sectionData.items.some(item =>
                !showOnlyMissing || item.missing || item.values.includes('Тег не найден')
            );

            if (!hasVisibleItems) return null;

            return (
                <React.Fragment key={sectionPath}>
                    <SectionHeaderRow onClick={() => handleToggleSection(sectionPath)}>
                        <TableCell colSpan={4}>
                            <Box display="flex" alignItems="center">
                                <IconButton size="small" sx={{ color: 'common.white' }}>
                                    {openSections[sectionPath] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                                <HeaderText variant="subtitle1" component="span">
                                    {sectionData.title.toUpperCase()}
                                </HeaderText>
                            </Box>
                        </TableCell>
                    </SectionHeaderRow>

                    <TableRow>
                        <TableCell colSpan={4} sx={{ p: 0, borderBottom: 1, borderColor: 'divider' }}>
                            <Collapse in={openSections[sectionPath]} timeout="auto" unmountOnExit>
                                <Table size={isMobile ? "small" : "medium"}>
                                    <TableHead>
                                        <SubHeaderRow>
                                            <TableCell width={isMobile ? "15%" : "10%"}><SubHeaderText><center>№</center></SubHeaderText></TableCell>
                                            <TableCell width={isMobile ? "25%" : "30%"}><SubHeaderText><center>Наименование</center></SubHeaderText></TableCell>
                                            <TableCell width={isMobile ? "20%" : "20%"}><SubHeaderText><center>Тег</center></SubHeaderText></TableCell>
                                            <TableCell width={isMobile ? "40%" : "40%"}><SubHeaderText><center>Значение</center></SubHeaderText></TableCell>
                                        </SubHeaderRow>
                                    </TableHead>
                                    <TableBody>
                                        {sectionData.items
                                            .filter(item => !item.tag.includes('_table_'))
                                            .filter(item => !showOnlyMissing || item.missing || item.values.includes('Тег не найден'))
                                            .sort((a, b) => (a.indicator_number || '').localeCompare(b.indicator_number || '', undefined, { numeric: true }))
                                            .map((item, idx) => {
                                                const isMissing = item.values.includes('Тег не найден') || item.missing;
                                                const indentSize = isMobile ? 20 : 28;
                                                const isSubsection = item.indicator_number && item.indicator_number.includes('.');

                                                const displayNumber = item.indicator_number || '';

                                                const indentLevel = isSubsection ? 1 : 0;

                                                return (
                                                    <StyledTableRow key={`${sectionPath}-${idx}`} even={idx % 2 === 0} missing={isMissing}>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{
                                                                paddingLeft: `${indentLevel * indentSize}px`,
                                                                fontWeight: !isSubsection ? 'bold' : 'normal',
                                                                fontSize: isMobile ? '0.75rem' : '0.875rem'
                                                            }}>
                                                                {displayNumber}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{
                                                                fontStyle: isSubsection ? 'italic' : 'normal',
                                                                paddingLeft: `${indentLevel * indentSize}px`,
                                                                fontWeight: !isSubsection ? 'bold' : 'normal',
                                                                fontSize: isMobile ? '0.75rem' : '0.875rem'
                                                            }}>
                                                                {item.field_name}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{
                                                                fontFamily: 'monospace',
                                                                fontWeight: !isSubsection ? 'bold' : 'normal',
                                                                fontSize: isMobile ? '0.75rem' : '0.875rem'
                                                            }}>
                                                                {item.tag}
                                                            </Typography>
                                                        </TableCell>
                                                        <TextTableCell>
                                                            {isMissing ? (
                                                                <MissingValueText variant="body2">
                                                                    Тег не найден
                                                                </MissingValueText>
                                                            ) : item.values.length > 1 ? (
                                                                <MultiValueContainer>
                                                                    <MultiValueHeader onClick={() => handleToggleMultiValue(item.uniqueKey)}>
                                                                        <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                                            Показать {item.values.length} значений
                                                                        </Typography>
                                                                        <IconButton size="small">
                                                                            {openMultiValues[item.uniqueKey] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                                        </IconButton>
                                                                    </MultiValueHeader>
                                                                    <Collapse in={openMultiValues[item.uniqueKey]}>
                                                                        <MultiValueContent>
                                                                            {item.values.map((val, valIdx) => (
                                                                                <Box
                                                                                    key={valIdx}
                                                                                    mb={1}
                                                                                    pb={1}
                                                                                    borderBottom={valIdx < item.values.length - 1 ? `1px solid divider` : 'none'}
                                                                                >
                                                                                    {(typeof val === 'string' && val.startsWith('http'))? (
                                                                                        <StyledLink
                                                                                            href={val}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                        >
                                                                                            {val}
                                                                                        </StyledLink>
                                                                                    ) : (typeof val === 'string' && val.startsWith('<table')) ? (
                                                                                        <Button
                                                                                            variant="outlined"
                                                                                            size="small"
                                                                                            onClick={() => handleOpenModal(val, item.field_name)}
                                                                                        >
                                                                                            Просмотреть таблицу
                                                                                        </Button>
                                                                                    ) : (
                                                                                        <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                                                            {processText(val)}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Box>
                                                                            ))}
                                                                        </MultiValueContent>
                                                                    </Collapse>
                                                                </MultiValueContainer>
                                                            ) : (
                                                                item.values[0] && item.values[0].startsWith('<table') ? (
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={() => handleOpenModal(item.values[0], item.field_name)}
                                                                    >
                                                                        Просмотреть таблицу
                                                                    </Button>
                                                                ) : (
                                                                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                                        {processText(item.values[0] || 'Нет данных')}
                                                                    </Typography>
                                                                )
                                                            )}
                                                        </TextTableCell>
                                                    </StyledTableRow>
                                                );
                                            })}

                                        {Object.values(sectionData.tables || {})
                                            .flat()
                                            .filter(tableItem => !showOnlyMissing || tableItem.value === 'Тег не найден')
                                            .sort((a, b) => a.tag.localeCompare(b.tag))
                                            .map((tableItem, tableIdx) => {
                                                const tableNumber = tableItem.indicator_number || '';

                                                return (
                                                    <StyledTableRow
                                                        key={`table-${tableItem.tag}-${tableIdx}`}
                                                        even={tableIdx % 2 === 0}
                                                    >
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                                {tableNumber}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                                {tableItem.field_name}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                                {tableItem.tag}
                                                            </Typography>
                                                        </TableCell>
                                                        <TextTableCell>
                                                            {tableItem.value.startsWith('=HYPERLINK') ? (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        const url = tableItem.value.match(/http[^"]+/)[0];
                                                                        handleOpenModal(url, tableItem.field_name);
                                                                    }}
                                                                >
                                                                    Открыть таблицу
                                                                </Button>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                                    {processText(tableItem.value)}
                                                                </Typography>
                                                            )}
                                                        </TextTableCell>
                                                    </StyledTableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                </React.Fragment>
            );
        });
    };

    if (loading && !selectedOu) {
        return (
            <Container maxWidth="lg">
                <Box mt={5} textAlign="center">
                    <Typography variant="h6">Загрузка данных...</Typography>
                </Box>
            </Container>
        );
    }

    if (ous.length === 0) {
        return (
            <Container maxWidth="lg">
                <Box mt={5} textAlign="center">
                    <Typography variant="h6">Нет доступных образовательных учреждений</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ px: isMobile ? 1 : 3 }}>
            <Box mt={isMobile ? 3 : 5}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
                    Результаты проверки {selectedOu ? `ОУ №${ous.find(ou => ou.id === selectedOu)?.number}` : ''}

                </Typography>

                <Box mb={3} display="flex" flexDirection={isMobile ? "column" : "row"} alignItems={isMobile ? "stretch" : "center"} gap={2}>
                    {ous.length > 1 && (
                        <FormControl fullWidth variant="outlined" sx={{ minWidth: isMobile ? '100%' : 200 }}>
                            <InputLabel>Выберите ОУ</InputLabel>
                            <Select
                                value={selectedOu || ''}
                                onChange={(e) => setSelectedOu(e.target.value)}
                                label="Выберите ОУ"
                                size={isMobile ? "small" : "medium"}
                            >
                                {Array.from(new Set(ous.map(ou => ou.type)))
                                    .sort()
                                    .map(type => {
                                        const ousOfType = ous
                                            .filter(ou => ou.type === type)
                                            .sort((a, b) => parseInt(a.number) - parseInt(b.number));

                                        return [
                                            <ListSubheader key={`header-${type}`} sx={{ fontWeight: 'bold' }}>
                                                {type}
                                            </ListSubheader>,
                                            ...ousOfType.map(ou => (
                                                <MenuItem key={ou.id} value={ou.id} sx={{ pl: 4 }}>
                                                    {ou.number} - {ou.name || ou.type}
                                                </MenuItem>
                                            ))
                                        ];
                                    })}
                            </Select>
                        </FormControl>
                    )}


                    <Box display="flex" gap={2} width={isMobile ? '100%' : 'auto'}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCheckWebsite}
                            disabled={!selectedOu || isChecking}
                            fullWidth={isMobile}
                            size={isMobile ? "small" : "medium"}
                        >
                            Обновить данные
                        </Button>

                        <Button
                            variant="contained"
                            color="success"
                            onClick={exportToExcel}
                            disabled={!selectedOu || Object.keys(groupedResults).length === 0}
                            fullWidth={isMobile}
                            size={isMobile ? "small" : "medium"}
                        >
                            Экспорт в Excel
                        </Button>
                    </Box>

                    {isChecking && (
                        <Box width="100%">
                            <LinearProgress variant="determinate" value={progress} />
                            <Typography variant="caption">{progress}%</Typography>
                        </Box>
                    )}
                </Box>

                {selectedOu && (
                    <>
                        <center>Адрес сайта: <a href={ous.find(ou => ou.id === selectedOu)?.link} target={"_blank"}>{ous.find(ou => ou.id === selectedOu)?.link}</a></center>
                        <Box
                            display="flex"
                            flexDirection={isMobile ? "column" : "row"}
                            justifyContent="space-between"
                            alignItems={isMobile ? "stretch" : "center"}
                            mb={4}
                            p={isMobile ? 2 : 3}
                            bgcolor="background.paper"
                            borderRadius={2}
                            boxShadow={1}
                        >
                            <Box display="flex" alignItems="center" flexDirection="column" mb={isMobile ? 2 : 0}>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                                    Общий процент заполненности
                                </Typography>
                                <CircularProgressWithLabel value={completionPercentage} size={isMobile ? 120 : 150} thickness={isMobile ? 8 : 10} />
                                <Typography variant="body2" color="textSecondary" textAlign="center">
                                    {completionPercentage > 70 ? 'Высокий уровень заполнения' :
                                        completionPercentage > 40 ? 'Средний уровень заполнения' :
                                            'Низкий уровень заполнения'}
                                </Typography>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showOnlyMissing}
                                        onChange={() => setShowOnlyMissing(!showOnlyMissing)}
                                        color="primary"
                                        size={isMobile ? "small" : "medium"}
                                    />
                                }
                                label="Показать только отсутствующие теги"
                                labelPlacement={isMobile ? "end" : "start"}
                                sx={{ ml: 0, mt: isMobile ? 1 : 0 }}
                            />
                        </Box>

                        <TableContainer
                            component={Paper}
                            elevation={3}
                            sx={{
                                width: '100%',
                                overflowX: 'auto',
                                maxHeight: isMobile ? 'calc(100vh - 400px)' : 'none'
                            }}
                        >
                            <Table stickyHeader={isMobile}>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <Typography>Загрузка результатов...</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : renderTable()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Box>

            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="lg"
                fullWidth
                scroll="paper"
                fullScreen={isMobile}
            >
                <DialogTitle sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }}>{currentTableName}</DialogTitle>
                <DialogContent dividers>
                    {renderModalTable()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} size={isMobile ? "small" : "medium"}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{
                    vertical: isMobile ? 'bottom' : 'top',
                    horizontal: 'center'
                }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={error ? 'error' : 'success'}
                    elevation={6}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {error || success}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default UserResults;